import { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { field_can_autoadvance, field_can_autosubmit } from "@tellescope/utilities";
import { AnswerForType } from "@tellescope/types-models";
import { useCallback, useEffect, useRef } from "react";
import { areRequiredSubFieldsFilled, isChargebeeAnswerValue } from "../../utils";
import type { FormFieldNode, NextFieldLogicOptions } from "../../utils/types";
import type { FormShallowStoreHook } from "../../store";
import { FormTypesWithValues } from "../../types";
import type { LocalTellescopeFormResponseMap } from "../use-tellescope-form-core";
import type { ImmediateSaveAnswer } from "./use-save-field-response";

export interface UseAutoAdvanceArgs {
  setFieldValue: <TField extends FormTypesWithValues>(fieldId: string, value: AnswerForType[TField]) => void;
  fieldsMap: Record<string, TellescopeModelFromName<"form_fields">>;
  currentResponses: LocalTellescopeFormResponseMap;
  repeatMismatchFieldIds: Set<string>;
  logicOptions: NextFieldLogicOptions;
  activeField: FormFieldNode | undefined;
  goToNextFieldInternal: () => void;
  saveImmediateFieldResponse: (field: TellescopeModelFromName<"form_fields">, answer: ImmediateSaveAnswer) => void;
  useFormShallowStore: FormShallowStoreHook;
}

const AUTO_ADVANCE_DEBOUNCE_MS = 250;

export const useAutoAdvance = ({
  setFieldValue,
  fieldsMap,
  currentResponses,
  repeatMismatchFieldIds,
  logicOptions,
  activeField,
  goToNextFieldInternal,
  saveImmediateFieldResponse,
  useFormShallowStore,
}: UseAutoAdvanceArgs) => {
  const autoAdvanceFieldChangeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const latestRef = useRef({
    fieldsMap,
    currentResponses,
    repeatMismatchFieldIds,
    logicOptions,
    activeField,
    goToNextFieldInternal,
  });
  latestRef.current = {
    fieldsMap,
    currentResponses,
    repeatMismatchFieldIds,
    logicOptions,
    activeField,
    goToNextFieldInternal,
  };

  const submitFormRef = useRef<() => Promise<unknown>>(() => Promise.resolve());
  // Autosubmit must fire at most once per mount, so a failed attempt does not retry on the next
  // keystroke and a redirect-less success does not submit twice.
  const autoSubmitFiredRef = useRef(false);
  const {
    isAutoSubmitting,
    setIsAutoSubmitting,
    autoSubmitted,
    setAutoSubmitted,
    autoSubmitError,
    setAutoSubmitError,
    serverRedirectUrl,
  } = useFormShallowStore(
    "isAutoSubmitting",
    "setIsAutoSubmitting",
    "autoSubmitted",
    "setAutoSubmitted",
    "autoSubmitError",
    "setAutoSubmitError",
    "serverRedirectUrl"
  );

  // Cleanup of an external resource (a pending setTimeout) on unmount — the only
  // way to cancel a timer whose lifetime is tied to this hook's mount.
  useEffect(
    () => () => {
      if (autoAdvanceFieldChangeTimerRef.current) {
        clearTimeout(autoAdvanceFieldChangeTimerRef.current);
      }
    },
    []
  );

  const setFieldValueWithAutoAdvance = useCallback(
    <TField extends FormTypesWithValues>(fieldId: string, value: AnswerForType[TField]) => {
      setFieldValue(fieldId, value);

      const field = fieldsMap[fieldId];
      if (!field) return;

      if (field.type === "Chargebee" && isChargebeeAnswerValue(value)) {
        saveImmediateFieldResponse(field, { type: "Chargebee", value });
      } else if (field.type === "Stripe" && typeof value === "string") {
        saveImmediateFieldResponse(field, { type: "Stripe", value });
      } else if (field.type === "Appointment Booking" && typeof value === "string") {
        saveImmediateFieldResponse(field, { type: "Appointment Booking", value });
      }

      const canAutosubmit =
        !!field.options?.autoSubmit && field_can_autosubmit({ type: field.type, options: field.options });

      const canAutoAdvance =
        !!field.options?.autoAdvance && field_can_autoadvance({ type: field.type, options: field.options });

      if (!canAutosubmit && !canAutoAdvance) return;

      if (autoAdvanceFieldChangeTimerRef.current) {
        clearTimeout(autoAdvanceFieldChangeTimerRef.current);
      }
      autoAdvanceFieldChangeTimerRef.current = setTimeout(() => {
        autoAdvanceFieldChangeTimerRef.current = null;
        if (canAutosubmit) {
          if (autoSubmitFiredRef.current) return;
          autoSubmitFiredRef.current = true;
          setIsAutoSubmitting(true);
          submitFormRef.current().then(
            () => {
              setIsAutoSubmitting(false);
              setAutoSubmitted(true);
            },
            () => {
              setIsAutoSubmitting(false);
              setAutoSubmitError(true);
            }
          );
          return;
        }

        const latest = latestRef.current;
        if (latest.activeField?.value.type === "Question Group") {
          const allRequiredSubFieldsFilled = areRequiredSubFieldsFilled({
            group: latest.activeField.value,
            fieldsMap: latest.fieldsMap,
            responsesByFieldId: latest.currentResponses,
            allResponses: Object.values(latest.currentResponses),
            repeatMismatchFieldIds: latest.repeatMismatchFieldIds,
            logicOptions: latest.logicOptions,
          });
          if (!allRequiredSubFieldsFilled) return;
        }

        latest.goToNextFieldInternal();
      }, AUTO_ADVANCE_DEBOUNCE_MS);
    },
    [setFieldValue, fieldsMap, saveImmediateFieldResponse, setIsAutoSubmitting, setAutoSubmitted, setAutoSubmitError]
  );

  return {
    setFieldValueWithAutoAdvance,
    submitFormRef,
    isAutoSubmitting,
    autoSubmitted,
    autoSubmitError,
    serverRedirectUrl,
  };
};
